import { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "@/lib/motion";

/**
 * Drives a scene through numbered stages (0..steps).
 *
 * The scenes used to advance a stage at a time as the reader scrolled through
 * an artificially tall wrapper with a sticky inner block. That produced long
 * stretches of dead scroll and a picture that changed in steps rather than
 * with the hand, which read as lag. The end state of every scene carries the
 * whole message, so the end state is now what every reader gets, immediately,
 * at every viewport. (CMD_01, 18 Sep 2026.)
 *
 * The hook keeps its signature so no caller has to change: `stage` is the
 * final stage, `heavy` is false, `wrapperStyle` is undefined so the sections
 * take their natural height, and `inView` is true so LivingPhoto keeps
 * rendering the moving stream dots, which are an SVG loop and never were
 * scroll-bound.
 */
export function useScrollStage(steps) {
  const ref = useRef(null);
  return { ref, stage: steps, heavy: false, inView: true, wrapperStyle: undefined };
}

/**
 * Was: play a stage sequence on a timer once the block came into view.
 * Now: the finished sequence, shown at once, for the same reason as above.
 * `replay` is kept so callers do not break, and does nothing.
 */
export function useTimedStage(steps) {
  const ref = useRef(null);
  return { ref, stage: steps, inView: true, replay: () => {} };
}

/** True once the element has been at least `threshold` visible. Stays true. */
export function useInView(threshold = 0.35) {
  const ref = useRef(null);
  const [on, setOn] = useState(() => prefersReducedMotion());
  useEffect(() => {
    const el = ref.current;
    if (!el || on) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [on, threshold]);
  return { ref, on };
}
